import * as Sentry from "@sentry/vue"
import { createPinia, PiniaVuePlugin } from "pinia"
import Vue from "vue"
import VueYandexMetrika from "vue-yandex-metrika"
import VueGtag from "vue-gtag"
import VueGtm from "@gtm-support/vue2-gtm"
import LocaleProvider from "@/components/Common/LocaleProvider/LocaleProvider.vue"
import router from "./router"
import store from "./store"
import i18n from "./i18n"
import VueMeta from "vue-meta"
import Antd from "ant-design-vue"
import { API, ADS_API, TM_API, PM_API } from "./utils/http.js"
import moment from "moment"
import "moment/locale/uk"
import "./assets/less/_ant-design-style-imports.less"
import "./assets/scss/_main.scss"
import "./assets/scss/_ant-table-filter-dropdown-btns.scss"
import "./assets/scss/_ant-fix.scss"
import lazyLoad from "@/directives/lazyLoad"
import onRowClick from "@/directives/onRowClick"
import { getCookie } from "@/utils/cookies"
import "./registerServiceWorker"

const accessToken = getCookie("auth_access_mpo")

if (accessToken) {
  const jwtToken = `Bearer ${accessToken}`

  API.defaults.headers.common["Authorization"] = jwtToken
  ADS_API.defaults.headers.common["Authorization"] = jwtToken
  TM_API.defaults.headers.common["Authorization"] = jwtToken
  PM_API.defaults.headers.common["Authorization"] = jwtToken
}

moment.locale(store.state.common.currentLocale)

Vue.prototype.$moment = moment
Vue.config.productionTip = false

Vue.use(Antd)
Vue.use(VueMeta, { refreshOnceOnNavigation: true })
Vue.use(VueGtag, { config: { id: process.env.VUE_APP_ENV_GTAG_CONFIG } }, router)

Vue.use(PiniaVuePlugin)
const pinia = createPinia()

Vue.directive("lazyload", lazyLoad)
Vue.directive("on-row-click", onRowClick)

const USE_ANALYTICS = process.env.VUE_APP_ANALYTICS === "true"

if (USE_ANALYTICS) {
  const GTAG_KEY = process.env.VUE_APP_ENV_GTAG_KEY
  const GTM_KEY = process.env.VUE_APP_ENV_GTM_KEY
  const YM_KEY = process.env.VUE_APP_ENV_YM_KEY

  if (GTAG_KEY) {
    Vue.use(VueGtag, { config: { id: GTAG_KEY } }, router)
  }

  if (GTM_KEY) {
    Vue.use(VueGtm, {
      id: GTM_KEY,
      debug: true,
      loadScript: true,
      vueRouter: router,
      trackOnNextTick: true
    })
  }

  if (YM_KEY) {
    Vue.use(VueYandexMetrika, {
      id: process.env.VUE_APP_ENV_YM_KEY,
      router,
      env: "production",
      scriptSrc: "https://cdn.jsdelivr.net/npm/yandex-metrica-watch/tag.js",
      options: {
        defer: true,
        clickmap: true,
        trackLinks: true,
        accurateTrackBounce: true,
        webvisor: true
      }
    })
  }
}

if (process.env.VUE_APP_SENTRY_ENABLED === "true") {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    replaysSessionSampleRate: Number(process.env.VUE_APP_SENTRY_REPLAY_SESSION_SAMPLE_RATE) || 0.1,
    replaysOnErrorSampleRate: Number(process.env.VUE_APP_SENTRY_REPLAY_ERROR_SAMPLE_RATE) || 1.0,
    tracesSampleRate: Number(process.env.VUE_APP_SENTRY_TRACES_SAMPLE_RATE) || 0.1,
    profilesSampleRate: Number(process.env.VUE_APP_SENTRY_PROFILES_SAMPLE_RATE) || 0.1,
    environment: process.env.VUE_APP_SENTRY_ENVIRONMENT as string
  })
}

new Vue({
  store,
  pinia,
  router,
  i18n,
  render: (h) => h(LocaleProvider)
}).$mount("#app")
